import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useLazyGetCustomerOrgListQuery } from '../services/orgSelectionApi';
import { endSession } from '../../../services/auth-token';
import { showAlert } from '../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import { switchEntity } from '../../../services/entity-scope';
import { isSessionEnding } from '../../../services/switch-errors';
import type {
  AccessibleOrg,
  AdminOrgItem,
} from '../../../types/org-selection.types';

/**
 * Discriminated union returned by `fetchOrgData()` so callers narrow without
 * `as unknown as` laundering. The data branches map to the two backend
 * endpoints (customer = user-service `EntityDetails`, normalised to
 * `AccessibleOrg`; admin = entity-service `Entity`); see
 * `org-selection.types.ts` for the field-level rationale.
 *
 * `'error'` carries no payload on purpose: the caller needs to know the list
 * is UNKNOWN, not why. Before DV-3012 the catch returned an empty customer
 * list instead, which made a failed fetch indistinguishable from a user with
 * genuinely zero accounts — see `fetchOrgData`.
 */
export type OrgListResult =
  | { kind: 'customer'; data: AccessibleOrg[] }
  | { kind: 'admin'; data: AdminOrgItem[] }
  | { kind: 'error' };

export function useOrgSelection() {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const userId = localStorage.getItem('userId') ?? '';

  const [triggerCustomerOrgList] = useLazyGetCustomerOrgListQuery();

  const selectOrg = async (
    companyName: string,
    role: string,
    entityId: string,
    parentEntityId?: string,
  ) => {
    setIsLoading(true);
    try {
      // For sub-entity selection (deep-link recovery, resolved by the
      // bootstrap's ancestor walk), parentEntityId is the top-level org we're
      // anchoring to; entityId is the leaf the user lands on. A plain org pick
      // anchors to itself.
      await switchEntity(
        {
          entityId,
          entityName: companyName,
          role,
          parentEntityId: parentEntityId ?? entityId,
        },
        'land-intended',
      );
    } catch (error) {
      // The session is ending and its navigation is pending: keep the spinner,
      // say nothing (DV-3421 review round 5, item 4).
      if (isSessionEnding(error)) return;
      setIsLoading(false);
      dispatch(
        showAlert({ type: Variants.ERROR, message: 'Something went wrong, Please contact admin' }),
      );
    }
  };

  /**
   * The customer org list.
   *
   * <p>DV-2958: the internal-admin branch is gone. It called
   * `companyListView=true`, whose only caller left was
   * `OrganizationSelectionPage` — which discarded the admin result outright
   * (`result.kind === 'customer' ? result.data : []`), so an admin who
   * hard-refreshed that page paid ~35 s of cross-service aggregation for a list
   * that was thrown away. Internal admins get their accounts from the picker and
   * the accounts console now, so there was nothing left to serve.
   *
   * <p>DV-3012 left this with one narrow caller: `OrganizationSelectionPage`
   * calls it on mount ONLY when the cached `OrgData` is empty, which happens
   * when the resolution step's org-list fetch threw and its catch routed the
   * user here anyway. Everything else reads the list the step already wrote.
   *
   * <p>A failure is REPORTED, not laundered. This used to swallow the error
   * and resolve with an empty customer list, which its one caller could not
   * tell apart from "this user has no accounts" — so a blip on the fallback
   * fetch persisted `[]` to `OrgData` and rendered a permanent-looking empty
   * account chooser whose only control was Sign Out. Returning `'error'`
   * makes that state representable, and nothing is persisted on it.
   */
  const fetchOrgData = async (): Promise<OrgListResult> => {
    setIsLoading(true);
    try {
      const data = await triggerCustomerOrgList(userId).unwrap();
      return { kind: 'customer', data };
    } catch (error) {
      console.error('Failed to fetch OrgData', error);
      return { kind: 'error' };
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, selectOrg, endSession, fetchOrgData };
}
